import React from "react";
import { Link } from "react-router-dom";
import SearchBox from "../components/SearchBox";
import { featuredDestinations } from "../data/mockData";
import {
  Star,
  MapPin,
  Shield,
  Award,
  Trophy,
  Star as StarIcon,
  ShieldCheck,
  Globe,
} from "lucide-react";

const HomePage: React.FC = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-teal-500/20 via-transparent to-teal-900/30"></div>

        {/* Floating Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-16 h-16 sm:w-20 sm:h-20 bg-white/10 rounded-full animate-float-slow"></div>
          <div className="absolute top-40 right-20 w-12 h-12 sm:w-16 sm:h-16 bg-teal-300/20 rounded-full animate-float-medium"></div>
          <div className="absolute bottom-40 left-20 w-8 h-8 sm:w-12 sm:h-12 bg-white/15 rounded-full animate-float-fast"></div>
          <div className="absolute bottom-20 right-40 w-20 h-20 sm:w-24 sm:h-24 bg-teal-200/10 rounded-full animate-float-slow"></div>
        </div>

        {/* Geometric Patterns */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-24 h-24 sm:w-32 sm:h-32 border border-white/30 rotate-45 animate-spin-slow"></div>
          <div className="absolute bottom-1/4 right-1/4 w-16 h-16 sm:w-24 sm:h-24 border border-white/20 rotate-12 animate-pulse"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto text-center">
            {/* Animated Title */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-white via-teal-100 to-white bg-clip-text text-transparent animate-fade-in-up leading-tight">
                Discover Egypt with
              </h1>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-teal-200 via-white to-teal-200 bg-clip-text text-transparent animate-fade-in-up-delay leading-tight">
                ETB Tours
              </h1>
              <div className="inline-block px-4 py-2 sm:px-6 sm:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white text-lg sm:text-xl font-bold rounded-full animate-bounce-gentle">
                Culture & Red Sea
              </div>
            </div>

            {/* Animated Subtitle */}
            <div className="mb-6 sm:mb-8 animate-fade-in-up-delay-2">
              <p className="text-lg sm:text-xl md:text-2xl mb-3 sm:mb-4 text-teal-100 font-medium px-4">
                Egypt - Jordan - United Arab Emirates - Palestine - Türkiye -
                Morocco
              </p>
              <p className="text-base sm:text-lg text-teal-200 max-w-4xl mx-auto leading-relaxed px-4">
                Over 30 years of success crafting exceptional journeys with
                carefully curated itineraries, blending cultural depth with
                unforgettable experiences.
              </p>
            </div>

            {/* Enhanced Search Box */}
            <div className="max-w-5xl mx-auto animate-fade-in-up-delay-3 px-4">
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 sm:p-6 lg:p-8 border border-white/20 shadow-2xl">
                <SearchBox variant="hero" />
              </div>
            </div>

            {/* Floating Action Buttons */}
            <div className="mt-8 mb-4 sm:mt-12 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center animate-fade-in-up-delay-4 px-4">
              <button className="group relative px-6 py-3 sm:px-8 sm:py-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white font-bold rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 text-sm sm:text-base">
                <span className="relative z-10">Start Your Journey</span>
                <div className="absolute inset-0 bg-gradient-to-r from-teal-400 to-teal-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
              <button className="group px-6 py-3 sm:px-8 sm:py-4 border-2 border-white/50 text-white font-bold rounded-full hover:bg-white/10 transition-all duration-300 backdrop-blur-sm text-sm sm:text-base">
                <span className="group-hover:scale-110 transition-transform duration-300 inline-block">
                  Watch Video
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/70 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Featured Destinations */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-teal-50/30 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-32 h-32 border border-teal-300 rounded-full"></div>
          <div className="absolute top-32 right-20 w-24 h-24 border border-teal-200 rounded-full"></div>
          <div className="absolute bottom-20 left-1/4 w-16 h-16 border border-teal-400 rounded-full"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="text-sm font-semibold text-teal-600 bg-teal-100 px-4 py-2 rounded-full">
                Discover Egypt
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 bg-gradient-to-r from-gray-900 via-teal-800 to-gray-900 bg-clip-text text-transparent">
              Amazing tours and holidays in Egypt
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Embark on unforgettable adventures with the most extensive and
              premium selection of experiences, tours, and activities across
              Egypt, all offered at unbeatable prices.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {featuredDestinations.map((destination, index) => (
              <Link
                key={destination.id}
                to={`/search?location=${encodeURIComponent(destination.name)}`}
                className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-4 hover:rotate-1"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Glassmorphism Effect */}
                <div className="absolute inset-0 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl"></div>

                {/* Image Container */}
                <div className="relative h-64 overflow-hidden rounded-2xl">
                  <img
                    src={destination.image}
                    alt={destination.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent group-hover:from-black/60 transition-all duration-500"></div>

                  {/* Floating Badge */}
                  <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 text-white text-sm font-semibold border border-white/30">
                    {destination.hotelCount} tours
                  </div>
                </div>

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2 group-hover:text-teal-200 transition-colors duration-300">
                    {destination.name}
                  </h3>
                  <p className="text-gray-200 mb-4 text-sm font-medium">
                    {destination.country}
                  </p>

                  {/* Price Badge */}
                  <div className="flex items-center justify-between">
                    <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                      From ${destination.averagePrice}/night
                    </div>
                    <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-teal-500 transition-colors duration-300">
                      <svg
                        className="w-4 h-4 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Hover Effect Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-teal-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"></div>
              </Link>
            ))}
          </div>

          {/* View All Button */}
          <div className="text-center mt-12">
            <button className="group relative px-8 py-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white font-bold rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
              <span className="relative z-10">View All Destinations</span>
              <div className="absolute inset-0 bg-gradient-to-r from-teal-500 to-teal-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-br from-teal-50 via-white to-gray-50 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-40 h-40 bg-teal-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-32 h-32 bg-teal-300 rounded-full blur-2xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="text-sm font-semibold text-teal-600 bg-teal-100 px-4 py-2 rounded-full">
                Why Choose Us
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 bg-gradient-to-r from-gray-900 via-teal-800 to-gray-900 bg-clip-text text-transparent">
              Why Choosing ETB Tours Egypt?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              ETB Tours crafts exceptional journeys with carefully curated
              itineraries, blending cultural depth with unforgettable
              experiences.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Star,
                title: "Over 30 years of success",
                description:
                  "ETB Tours has been setting standards in the local and regional tourism industry, and our years of experience have helped us craft finely-honed tour itineraries that never fail to delight.",
                color: "from-teal-500 to-teal-600",
                bgColor: "bg-teal-50",
              },
              {
                icon: MapPin,
                title: "Customized Tours",
                description:
                  "ETB Tours offers the flexibility of customized tours, tailoring experiences to match individual preferences and interests. Whether you're interested in historical sites, cultural immersion, or adventure, they can create an itinerary that suits your needs.",
                color: "from-blue-500 to-blue-600",
                bgColor: "bg-blue-50",
              },
              {
                icon: Shield,
                title: "Reliability and Trust",
                description:
                  "With a reputation for reliability and trustworthiness, ETB Tours has gained the confidence of travelers. Their commitment to delivering what they promise is reflected in positive reviews and satisfied clients.",
                color: "from-green-500 to-green-600",
                bgColor: "bg-green-50",
              },
              {
                icon: Award,
                title: "Ease of Booking and Communication",
                description:
                  "The booking process with ETB Tours is designed to be user-friendly, and communication channels are open and responsive, ensuring a smooth and hassle-free experience for clients.",
                color: "from-purple-500 to-purple-600",
                bgColor: "bg-purple-50",
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="group relative bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-white/20"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Hover Effect Background */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-500`}
                ></div>

                {/* Icon Container */}
                <div
                  className={`relative w-20 h-20 ${feature.bgColor} rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}
                >
                  <div
                    className={`w-12 h-12 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center shadow-lg`}
                  >
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                </div>

                {/* Content */}
                <div className="text-center">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-teal-700 transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                    {feature.description}
                  </p>
                </div>

                {/* Decorative Elements */}
                <div className="absolute top-4 right-4 w-8 h-8 bg-gradient-to-r from-teal-400 to-teal-500 rounded-full opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
                <div className="absolute bottom-4 left-4 w-6 h-6 bg-gradient-to-r from-teal-300 to-teal-400 rounded-full opacity-0 group-hover:opacity-30 transition-opacity duration-500"></div>
              </div>
            ))}
          </div>

          {/* Stats Section */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: "30+", label: "Years Experience" },
              { number: "50K+", label: "Happy Travelers" },
              { number: "100+", label: "Tour Packages" },
              { number: "24/7", label: "Customer Support" },
            ].map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="text-4xl md:text-5xl font-bold text-teal-600 mb-2 group-hover:scale-110 transition-transform duration-300">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 text-white relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-teal-500/20 to-transparent"></div>
          <div className="absolute bottom-0 right-0 w-full h-full bg-gradient-to-tl from-teal-800/30 to-transparent"></div>
        </div>

        {/* Floating Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-20 w-32 h-32 bg-white/5 rounded-full animate-float-slow"></div>
          <div className="absolute top-40 right-32 w-24 h-24 bg-teal-300/10 rounded-full animate-float-medium"></div>
          <div className="absolute bottom-32 left-32 w-20 h-20 bg-white/10 rounded-full animate-float-fast"></div>
          <div className="absolute bottom-20 right-20 w-28 h-28 bg-teal-200/5 rounded-full animate-float-slow"></div>
        </div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="inline-block mb-6">
              <span className="text-sm font-semibold text-teal-200 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                Start Your Journey
              </span>
            </div>

            <h2 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-teal-100 to-white bg-clip-text text-transparent">
              Ready to Start Your Egyptian Adventure?
            </h2>

            <p className="text-xl md:text-2xl mb-12 text-teal-100 max-w-3xl mx-auto leading-relaxed">
              Book your perfect tour today and create memories that last a
              lifetime. Experience the magic of ancient Egypt with our expert
              guides.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Link
                to="/search"
                className="group relative px-10 py-5 bg-white text-teal-600 font-bold rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 text-lg"
              >
                <span className="relative z-10">Inquire Now</span>
                <div className="absolute inset-0 bg-gradient-to-r from-teal-50 to-teal-100 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Link>

              <button className="group px-10 py-5 border-2 border-white/50 text-white font-bold rounded-full hover:bg-white/10 transition-all duration-300 backdrop-blur-sm text-lg">
                <span className="group-hover:scale-110 transition-transform duration-300 inline-block">
                  Download Brochure
                </span>
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                {
                  icon: Trophy,
                  label: "Award Winning",
                  color: "text-yellow-400",
                },
                {
                  icon: StarIcon,
                  label: "5-Star Rated",
                  color: "text-yellow-400",
                },
                {
                  icon: ShieldCheck,
                  label: "Fully Insured",
                  color: "text-green-400",
                },
                {
                  icon: Globe,
                  label: "Global Tours",
                  color: "text-blue-400",
                },
              ].map((item, index) => (
                <div key={index} className="text-center group">
                  <div className="flex justify-center mb-3">
                    <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 border border-white/20">
                      <item.icon className={`w-8 h-8 ${item.color}`} />
                    </div>
                  </div>
                  <div className="text-teal-200 font-medium">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
